import React, { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { http } from "../../lib/http";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { CheckCircle2, AlertTriangle, Lock } from "lucide-react";

export default function SetPassword() {
    console.log("SetPassword component rendered DEBUG v5");
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const token = searchParams.get("token");
    const emailParam = searchParams.get("email");

    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [showPasswords, setShowPasswords] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState(false);

    useEffect(() => {
        if (!token) {
            setError("Invalid or missing activation token. Please check your email link.");
        }
    }, [token]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError("");

        const trimmedPassword = password.trim();
        const trimmedConfirm = confirmPassword.trim();

        if (trimmedPassword !== trimmedConfirm) {
            setError("Passwords do not match. Please ensure both fields are exactly the same.");
            setLoading(false);
            return;
        }

        // Standard validation (Matching backend's password policy)
        // Requires: 8+ chars, Uppercase, Lowercase
        const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z]).{8,}$/;
        if (!passwordRegex.test(trimmedPassword)) {
            setError("Password must be at least 8 characters long and include an uppercase and lowercase letter.");
            setLoading(false);
            return;
        }

        try {
            console.log("SENDING TO BACKEND:", {
                token,
                email: emailParam,
                password: trimmedPassword,
                confirmPassword: trimmedConfirm
            });
            const res = await http.post("/auth/set-password", {
                token,
                email: emailParam,
                password: trimmedPassword,
                confirmPassword: trimmedConfirm
            });

            if (res.data.ok) {
                setSuccess(true);
                setTimeout(() => {
                    navigate("/login");
                }, 3000);
            } else {
                setError(res.data.message || "Failed to set password");
            }
        } catch (err) {
            console.error("Set password error:", err);
            setError(err.response?.data?.message || "An error occurred while setting your password. The link might be expired.");
        } finally {
            setLoading(false);
        }
    };

    if (success) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
                <Card className="max-w-md w-full bg-white shadow-xl border-t-4 border-green-500">
                    <CardHeader className="text-center">
                        <CheckCircle2 className="w-16 h-16 text-green-500 mx-auto mb-4" />
                        <CardTitle className="text-2xl font-bold text-gray-900">Success!</CardTitle>
                        <CardDescription className="text-gray-600 text-lg">
                            Your password has been set successfully.
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="text-center">
                        <p className="text-gray-500 mb-6 font-medium">Redirecting to login page...</p>
                        <Button onClick={() => navigate("/login")} className="w-full bg-orange-600 hover:bg-orange-700 text-white py-6 text-lg font-bold transition-all duration-200">
                            Go to Login Now
                        </Button>
                    </CardContent>
                </Card>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
            <Card className="max-w-md w-full bg-white shadow-2xl border-t-4 border-orange-500 overflow-hidden">
                <div className="bg-orange-50 p-6 text-center border-b border-orange-100">
                    <div className="bg-orange-500 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg shadow-orange-200">
                        <Lock className="text-white w-8 h-8" />
                    </div>
                    <CardTitle className="text-2xl font-bold text-gray-900">Set Account Password</CardTitle>
                    <CardDescription className="text-orange-700 font-medium mt-1">
                        Complete your account setup to access the portal
                    </CardDescription>
                </div>

                <CardContent className="pt-8 px-8 pb-10">
                    <form onSubmit={handleSubmit} className="space-y-6">
                        {error && (
                            <div className="bg-red-50 border-l-4 border-red-500 text-red-700 p-4 rounded-md shadow-sm flex gap-3 items-start animate-in fade-in slide-in-from-top-2 duration-300">
                                <AlertTriangle className="w-5 h-5 mt-0.5 shrink-0" />
                                <p className="text-sm font-medium">{error}</p>
                            </div>
                        )}

                        <div className="space-y-4">
                            <div className="relative">
                                <label className="block text-sm font-semibold text-gray-700 mb-1.5 ml-1">New Password</label>
                                <Input
                                    type={showPasswords ? "text" : "password"}
                                    value={password}
                                    onChange={e => setPassword(e.target.value)}
                                    className="h-12 border-gray-200 focus:border-orange-500 focus:ring-orange-500 bg-white pr-10"
                                    placeholder="Enter your new password"
                                    required
                                    autoFocus
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPasswords(!showPasswords)}
                                    className="absolute right-3 top-[38px] text-gray-400 hover:text-gray-600 transition-colors text-xs font-semibold"
                                >
                                    {showPasswords ? "HIDE" : "SHOW"}
                                </button>
                                <p className="text-xs text-gray-500 mt-1.5 ml-1">Min. 8 characters (Uppercase + Lowercase)</p>
                            </div>

                            <div className="relative">
                                <label className="block text-sm font-semibold text-gray-700 mb-1.5 ml-1">Confirm Password</label>
                                <Input
                                    type={showPasswords ? "text" : "password"}
                                    value={confirmPassword}
                                    onChange={e => setConfirmPassword(e.target.value)}
                                    className="h-12 border-gray-200 focus:border-orange-500 focus:ring-orange-500 bg-white pr-10"
                                    placeholder="Re-enter your password"
                                    required
                                />
                            </div>
                        </div>

                        <Button
                            type="submit"
                            disabled={loading || !token}
                            className="w-full bg-orange-600 hover:bg-orange-700 text-white font-bold py-6 text-lg transition-all duration-200 shadow-lg shadow-orange-100 disabled:opacity-50 mt-4 h-14"
                        >
                            {loading ? (
                                <div className="flex items-center gap-2">
                                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                    <span>Setting Password...</span>
                                </div>
                            ) : "Activate My Account"}
                        </Button>

                        <div className="text-center mt-6">
                            <button
                                type="button"
                                onClick={() => navigate("/login")}
                                className="text-sm text-gray-500 hover:text-orange-600 font-medium transition-colors"
                            >
                                Back to Login
                            </button>
                        </div>
                    </form>
                </CardContent>
            </Card>
        </div>
    );
}
